import { Bounce, toast, ToastOptions } from "react-toastify";
import { TOAST_POSITION, TOAST_TYPE } from "../constants";
import { AWS_BASE_URL, DOMAIN_URL, GOOGLE_API_KEY } from "../clientConfig";
import moment, { DurationInputArg2 } from "moment";
import { SidebarItem } from "../types";
import axios from "axios";

export function isJson(data: string) {
  try {
    JSON.parse(data);
    return true;
  } catch (e) {
    return false;
  }
}

export const getLocalStorage = (key: string) => {
  if (typeof window !== "undefined") {
    const isValidJson = isJson(localStorage.getItem(key) ?? "");
    if (isValidJson) {
      const data = localStorage.getItem(key);
      return JSON.parse(data ?? "");
    }
    return localStorage.getItem(key);
  }
  return null;
};

export const setLocalStorage = (key: string, value: any) => {
  let lvalue = value;
  if (typeof value === "object") {
    lvalue = JSON.stringify(value);
  }
  localStorage.setItem(key, lvalue);
};

export const removeItemFromLocalStorage = (key: string) => {
  localStorage.removeItem(key);
};

export const clearLocalStorage = () => {
  localStorage.clear();
};

export function setCookie(
  name: string,
  value: any,
  exdays: number | null = null
) {
  let cvalue = value;
  if (typeof value === "object") {
    cvalue = JSON.stringify(value);
  }

  const d = new Date();
  if (exdays) {
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  }
  const expires: string = exdays ? "expires=" + d.toUTCString() : "";
  document.cookie = name + "=" + cvalue + ";" + expires + ";path=/";
}

export function getCookie(cname: string) {
  const name = cname + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      const isValidJson = isJson(c.substring(name.length, c.length) ?? "");
      if (isValidJson) {
        const data = c.substring(name.length, c.length);
        return JSON.parse(data ?? "");
      }
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export const showToastMessage = (
  type = "default",
  message: string | Promise<any>, // Accept both string & promise,
  options?: ToastOptions & { toastId?: string } & any // Add toastId for overwriting
) => {
  const toastOptions = {
    position: TOAST_POSITION.TOP_RIGHT,
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
    newestOnTop: false,
    toastId: message,
    ...options,
  };

  if (toastOptions.position?.includes("bottom")) {
    toastOptions.newestOnTop = true;
  }

  // If `message` is a promise, use `toast.promise`
  if (message instanceof Promise) {
    return toast.promise(
      message, // The promise itself
      {
        pending: options?.pending || "Processing...", // Message while pending
        success: options?.success || "Action completed successfully!", // Success message
        error: options?.error || "Something went wrong!", // Error message
      },
      toastOptions
    );
  }

  // Check if a toastId exists
  if (options?.toastId && toast.isActive(options.toastId)) {
    // Update the existing toast
    toast.update(options.toastId, {
      ...toastOptions,
      render: message, // Update the message
    });
  } else {
    // Create a new toast
    switch (type) {
      case TOAST_TYPE.SUCCESS:
        return toast.success(message, toastOptions);
      case TOAST_TYPE.ERROR:
        return toast.error(message, toastOptions);
      case TOAST_TYPE.WARNING:
        return toast.warning(message, toastOptions);
      case TOAST_TYPE.INFO:
        return toast.info(message, toastOptions);
      default:
        return toast(message, toastOptions);
    }
  }
};

export const generatePermalink = (title: string, path: string = ""): string => {
  const sanitizedTitle = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-"); // Replace spaces with dashes
  const randomString = Math.random().toString(36).substring(2, 12); // Generate random string
  return `${DOMAIN_URL}${path}/${sanitizedTitle}-${randomString}`;
};

export const generateSlug = (value: string): string => {
  const sanitizedTitle = value
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    ?.trim()
    ?.replace(/\s+/g, "-"); // Replace spaces with dashes

  return sanitizedTitle;
};

export const getCityAndState = (
  array: google.maps.GeocoderAddressComponent[] | undefined
) => {
  let city = "";
  let state = "";
  let postalCode = "";

  if (array?.length) {
    array.forEach((item: any) => {
      if (item.types.includes("administrative_area_level_3")) {
        city = item.long_name;
      }
      if (item.types.includes("administrative_area_level_1")) {
        state = item.long_name;
      }
      if (item.types.includes("postal_code")) {
        postalCode = item.long_name;
      }
    });
  }

  return { city, state, postalCode };
};

export const filterSidebarItems = (
  items: SidebarItem[],
  permissions: { module_name: string; isActive: boolean }[]
) => {
  return items.filter((item: SidebarItem) => {
    const permission = permissions?.find((p) => p?.module_name === item?.name);
    if (permission && permission?.isActive) {
      // if (item.children) {
      //   item.children = filterSidebarItems(item.children, permissions);
      // }
      return true;
    }
    return false;
  });
};

export const dateTimeFormat = (
  date: string,
  format: string = "YYYY-MM-DD hh:mm:ss"
): string => {
  if (date) {
    const convertDateTime: string = moment(date).format(format);
    return convertDateTime;
  }
  return "";
};

export function getNextDays(daysCount: number = 10) {
  const daysArray = [];

  for (let i = 0; i < daysCount; i++) {
    daysArray.push(moment().add(i, "days").format("YYYY-MM-DD"));
  }

  return daysArray;
}

export const getLatLong = async (): Promise<{
  latitude: number;
  longitude: number;
} | null> => {
  if (!navigator.geolocation) {
    console.error("Geolocation is not supported by this browser.");
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        resolve({ latitude, longitude });
      },
      (error) => {
        console.error("Error getting location:", error);
        resolve(null);
      }
    );
  });
};

export const momentDateAdjusterFormat = (
  value: number,
  type: DurationInputArg2,
  format: string = "YYYY-MM-DD hh:mm:ss",
  workType: "before" | "after" = "before"
): any => {
  const momentInstance = moment().clone();

  const convertDateTime =
    workType === "before"
      ? momentInstance.subtract(value, type).format(format)
      : momentInstance.add(value, type).format(format);

  return convertDateTime;
};

export const formatPrice = (price: number, floatPoints: number = 2) => {
  if (price >= 1_00_00_000) {
    return (
      (price / 1_00_00_000)?.toFixed(floatPoints).replace(/\.0$/, "") + " Cr"
    ); // Crores
  } else if (price >= 1_00_000) {
    return (price / 1_00_000)?.toFixed(floatPoints).replace(/\.0$/, "") + " L"; // Lakhs
  } else if (price >= 1_000) {
    return (price / 1_000)?.toFixed(floatPoints).replace(/\.0$/, "") + " K"; // Thousands
  } else {
    return price; // Less than 1000
  }
};

export const debounceFn = (func: any, delay: number) => {
  let timeout: any = null;

  const debounced = (...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
};

export const getIPAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const responseData = await response.json();
    return responseData?.ip;
  } catch (error) {
    console.error("Error while getting IP address", error);
  }
};

export function getNextHour() {
  const current = new Date();

  // If minutes are greater than 0, add 1 hour and reset minutes and seconds
  if (current.getMinutes() > 0 || current.getSeconds() > 0) {
    current.setHours(current.getHours() + 1);
  }

  // Set minutes and seconds to 0
  current.setMinutes(0, 0, 0);

  // Format the time as hh:mm AM/PM
  const options: any = { hour: "numeric", minute: "numeric", hour12: true };
  return current.toLocaleString("en-US", options);
}

// Helper function to handle pluralization
const pluralize = (value: number, unit: string) => {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
};

export const getUpdateDuration = (date: string) => {
  const now = moment();
  const then = moment(date);
  const diffInSeconds = now.diff(then, "seconds");
  const duration = moment.duration(diffInSeconds, "seconds");

  if (diffInSeconds < 60) {
    const seconds = Math.floor(duration.asSeconds());
    return pluralize(seconds, "second");
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(duration.asMinutes());
    return pluralize(minutes, "minute");
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(duration.asHours());
    return pluralize(hours, "hour");
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(duration.asDays());
    return pluralize(days, "day");
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(duration.asMonths());
    return pluralize(months, "month");
  } else {
    const years = Math.floor(duration.asYears());
    return pluralize(years, "year");
  }
};

export const convertToCapitalize = (text: string) => {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const downloadFn = (url: string) => {
  // Create a promise without making the executor async
  const downloadPromise = new Promise((resolve, reject) => {
    // Wrap the async logic in an immediately invoked async function
    (async () => {
      try {
        const response = await fetch(`${AWS_BASE_URL}${url}`);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const contentLength = response.headers.get("content-length");
        if (!contentLength) {
          throw new Error("Unable to determine file size.");
        }

        const totalSize = parseInt(contentLength, 10);
        let loaded = 0;

        const reader = response.body?.getReader();
        const chunks = [];
        let readCompleted = false;
        if (reader) {
          while (!readCompleted) {
            const { done, value } = await reader.read();
            if (done) {
              readCompleted = true;
            } else {
              chunks.push(value);
              loaded += value.length;

              // Update toast with progress percentage
              const progress = Math.round((loaded / totalSize) * 100);
              toast.update("download-progress", {
                render: `Downloading... ${progress}%`,
              });
            }
          }
        }

        // Create a blob from downloaded chunks
        const blob = new Blob(chunks);
        const fileExtension = url.split(".").pop()?.toLowerCase() || "file";
        const fileName = `${Date.now()}.${fileExtension}`;

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl); // Free up memory

        resolve("Downloaded successfully!");
      } catch (error) {
        window.open(`${AWS_BASE_URL}${url}`, "_blank", "noopener,noreferrer");
        console.error("Download failed.", error);
        reject("Download failed.");
      }
    })(); // Immediately invoke the async function
  });

  // Show toast messages using `showToastMessage` with the promise
  showToastMessage(TOAST_TYPE.INFO, downloadPromise, {
    pending: "Downloading...",
    success: { render: "Downloaded successfully!", autoClose: 3000 }, // Closes after 3s
    error: { render: "Download failed.", autoClose: 3000 }, // Closes after 3s
    toastId: "download-progress",
    autoClose: false, // Keep toast open during download
  });
};

export const isMobile = () => {
  const regex =
    /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
};

export async function getStateAndCityFromLatLong(
  lat: number,
  lng: number
): Promise<{
  state: string | null;
  city: string | null;
  postalCode?: string | null;
}> {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`;
  try {
    const response = await axios.get(url);
    const results = response.data.results;

    if (results.length > 0) {
      const addressComponents = results[0].address_components;

      let state: string | null = null;
      let city: string | null = null;
      let postalCode: string | null = null;

      // Loop through address components to find state and city
      for (const component of addressComponents) {
        if (component.types.includes("administrative_area_level_1")) {
          state = component.long_name; // Get state
        }
        if (component.types.includes("administrative_area_level_3")) {
          city = component.long_name; // Get city
        }
        if (component.types.includes("postal_code")) {
          postalCode = component.long_name;
        }
        // Exit early if both state and city are found
        if (state && city) break;
      }

      return { state, city, postalCode };
    }

    return { state: null, city: null, postalCode: null };
  } catch (error) {
    console.error("Error fetching geocoding data:", error);
    return { state: null, city: null, postalCode: null };
  }
}

// Utility function to calculate distance between two lat/lng points using Haversine formula
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

export const getSearchNearByPlace = async (
  nearByPlace: {
    types: { type: string; need: number }[];
    radius: number;
  } | null,
  lat: number,
  lng: number
) => {
  const hiddenDiv = document.createElement("div");
  const placesService = new google.maps.places.PlacesService(hiddenDiv);
  if (nearByPlace && placesService) {
    const allFacilities: any[] = [];
    for (const place of nearByPlace?.types ?? []) {
      const request = {
        location: { lat, lng },
        // radius: nearByPlace?.radius,
        rankBy: google.maps.places.RankBy.DISTANCE,
        type: place.type === "hotel" ? "lodging" : place.type,
      };

      const places = await new Promise<{ [key: string]: any[] }>(
        (resolve, reject) => {
          placesService.nearbySearch(request, async (results: any, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              const data = await Promise.all(
                results?.slice(0, place.need)?.map(async (item: any) => {
                  const roadDistance = await calculateRoadDistance(
                    { lat, lng },
                    {
                      lat: item.geometry.location.lat(),
                      lng: item.geometry.location.lng(),
                    }
                  );
                  return {
                    name: item?.name,
                    vicinity: item.vicinity,
                    distance: roadDistance.distance,
                    duration: roadDistance.duration,
                    lat: item.geometry.location.lat(),
                    long: item.geometry.location.lng(),
                    place_id: item.place_id,
                    img: item?.photos?.[0]?.getUrl(),
                  };
                })
              );
              resolve({
                [place.type]: data.sort(
                  (a: any, b: any) => a.distance - b.distance
                ),
              });
            } else {
              reject(`PlacesService failed: ${status}`);
            }
          });
        }
      );

      allFacilities.push(places);
    }
    return allFacilities;
  }
};

export const calculateRoadDistance = async (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<{ distance: number; duration: string }> => {
  return new Promise((resolve, reject) => {
    const service = new google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response: any, status) => {
        if (status === google.maps.DistanceMatrixStatus.OK) {
          const distance = response.rows[0].elements[0]?.distance?.value || 0; // Distance in meters
          const duration = response.rows[0].elements[0]?.duration?.text || "";
          resolve({ distance: distance / 1000, duration }); // Convert to kilometers
        } else {
          reject(`Distance Matrix API failed: ${status}`);
        }
      }
    );
  });
};

export const handleShowProjectPrice = (buildings: any) => {
  const prices = buildings?.map(
    (building: any) => building?.building_expected_price
  );
  if (prices?.length >= 2) {
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) {
      return formatPrice(minPrice) ? `₹${formatPrice(minPrice)}` : "";
    } else {
      return `₹${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
    }
  } else {
    return formatPrice(prices?.[0]) ? `₹${formatPrice(prices?.[0])}` : "";
  }
};

export const getProjectBuildingPrice = (data: any): string => {
  const prices = data?.buildings
    ?.map((building: any) => building?.building_expected_price)
    ?.filter((price: any) => price);

  if (prices) {
    if (prices?.length >= 2) {
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice === maxPrice) {
        return minPrice ? `₹ ${formatPrice(minPrice)}` : "";
      } else {
        return `₹ ${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
      }
    } else if (prices?.length >= 1) {
      return prices?.[0] ? `₹ ${formatPrice(prices?.[0])}` : "";
    } else return "";
  } else return "";
};

export const handleGetShowPrice = (data: any): string => {
  if (data?.type_project) {
    return getProjectBuildingPrice(data);
  } else if (data?.price) {
    return `₹ ${formatPrice(data?.price)}`;
  } else return "";
};

export const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

export const ageFormatDateDifferenceFn = (dateString: string) => {
  const startDate = moment(dateString);
  const now = moment();

  const years = now.diff(startDate, "years");
  startDate.add(years, "years"); // Adjust startDate forward
  const months = now.diff(startDate, "months");

  if (years === 0 && months > 0) {
    return months === 1 ? "1 month" : `${months} months`;
  } else if (years > 0 && months === 0) {
    return years === 1 ? "1 year" : `${years} years`;
  } else if (years > 0 && months > 0) {
    return `${years} year${years > 1 ? "s" : ""} ${months} month${months > 1 ? "s" : ""}`;
  } else {
    return "1 months";
  }
};

export const groupBy = (array: any[], keys: ((item: any) => any)[]) => {
  return array?.reduce(
    (result, currentValue, currentIndex) => {
      const groupKey = keys
        ?.map((keyFn) => keyFn(currentValue))
        .filter((val) => val)
        ?.join(" "); // or use JSON.stringify for uniqueness

      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push({ index: currentIndex, ...currentValue });
      return result;
    },
    {} as { [key: string]: any[] }
  );
};

export function removeItem(arr: any[], arrayIndex: number) {
  if (arrayIndex > -1) {
    arr.splice(arrayIndex, 1);
  }
  return arr;
}

export function buildQueryParams(
  data: Record<string, any>,
  includeZeroFields: string[] = []
): string {
  const params = new URLSearchParams();

  for (const key in data) {
    const value = data[key];

    const isZeroAllowed = includeZeroFields.includes(key);

    const shouldInclude =
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (value !== 0 || isZeroAllowed);

    if (shouldInclude) {
      params.append(key, value);
    }
  }

  return params.toString();
}
